import * as React from "react";
import Link from "next/link";
import { Check, ChevronRight } from "@/components/icons";

import { cn } from "@/lib/utils";

function GroupedList({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="grouped-list"
      className={cn("flex flex-col gap-6", className)}
      {...props}
    />
  );
}

function GroupedListSection({
  title,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"section">, "title"> & {
  title?: React.ReactNode;
}) {
  const headingId = React.useId();
  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={cn("flex flex-col gap-1.5", className)}
      {...props}
    >
      {title ? (
        <h2
          id={headingId}
          className="px-4 text-xs font-medium text-muted-foreground"
        >
          {title}
        </h2>
      ) : null}
      <ul className="divide-y divide-border-subtle overflow-hidden rounded-xl bg-surface">
        {children}
      </ul>
    </section>
  );
}

const ROW_CLASS =
  "flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left text-base transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none active:bg-surface-3";

interface GroupedListRowProps {
  href?: string;
  onSelect?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  trailing?: React.ReactNode;
  checked?: boolean;
  navigates?: boolean;
  tone?: "default" | "destructive";
  current?: boolean;
  children: React.ReactNode;
}

function GroupedListRowContent({
  icon: Icon,
  trailing,
  checked,
  navigates,
  tone,
  children,
}: Omit<GroupedListRowProps, "href" | "onSelect" | "current">) {
  return (
    <>
      {Icon ? (
        <Icon
          className={cn(
            "size-5 shrink-0",
            tone === "destructive" ? "text-current" : "text-muted-foreground",
          )}
          aria-hidden="true"
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing}
      {checked ? (
        <Check className="size-5 shrink-0 text-action" aria-hidden="true" />
      ) : null}
      {navigates ? (
        <ChevronRight
          className="size-4 shrink-0 text-fg-subtle"
          aria-hidden="true"
        />
      ) : null}
    </>
  );
}

function GroupedListRow({
  href,
  onSelect,
  current = false,
  ...content
}: GroupedListRowProps) {
  const className = cn(
    ROW_CLASS,
    content.tone === "destructive" ? "text-destructive-strong" : "text-foreground",
  );
  return (
    <li>
      {href !== undefined ? (
        <Link
          href={href}
          onClick={onSelect}
          aria-current={current ? "page" : undefined}
          className={className}
        >
          <GroupedListRowContent {...content} />
        </Link>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={content.checked}
          className={className}
        >
          <GroupedListRowContent {...content} />
        </button>
      )}
    </li>
  );
}

function GroupedListItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      className={cn(
        "flex min-h-11 items-center gap-3 px-4 py-2.5 text-base",
        className,
      )}
      {...props}
    />
  );
}

export { GroupedList, GroupedListSection, GroupedListRow, GroupedListItem };
